/**
 * Live geometry of the app's main content area (`[data-app-main]`, set on
 * AppShell's content div) and the viewport, for the assistant's
 * captive-takeover choreography (`AssistantHost.tsx`). Only measures while
 * `locked` — an onboarding takeover is running — so a `ResizeObserver` isn't
 * kept alive for the overwhelming majority of page views that never touch it.
 *
 * While `locked`, this ALSO locks the content area — `inert` (removes it
 * from focus/hit-testing), `aria-hidden` (removes it from the accessibility
 * tree), and `overflow: hidden` (no background scroll). Restored the instant
 * `locked` goes false.
 *
 * `pushed` (independent of `locked`) drives the "page pushes back" cue: a
 * CSS transition to `scale(0.98)`/`opacity 0.55` centered on the element,
 * un-pushing over its own transition duration the moment `pushed` goes
 * false — the caller sets `pushed` false a beat before `locked` (during the
 * takeover's `returning` phase) so the un-push overlaps the puck's flight
 * home instead of happening after it lands. Opacity only under reduced
 * motion — no scale.
 *
 * @module features/assistant/contentAreaRect
 */

import React from 'react';
import { useReducedMotion } from 'framer-motion';
import type { Rect } from './cornerPin';

/** Push-back transition duration, in ms — matches the CSS `transition` set below. */
export const PUSH_TRANSITION_MS = 350;

function measure(): Rect | null {
  const el = document.querySelector('[data-app-main]');
  const rect = el?.getBoundingClientRect();
  return rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null;
}

/**
 * The main content area's bounding rect. Re-measures on element resize and
 * window resize while `locked`, and locks/pushes the element as described
 * above; returns null when never activated.
 */
export function useContentAreaRect(locked: boolean, pushed: boolean): Rect | null {
  const reducedMotion = useReducedMotion();
  const [state, setState] = React.useState(() => measure());

  React.useEffect(() => {
    if (!locked) return;
    setState(measure());

    const el = document.querySelector('[data-app-main]') as HTMLElement | null;
    const onResize = () => setState(measure());

    const resizeObserver = el ? new ResizeObserver(onResize) : null;
    resizeObserver?.observe(el!);
    window.addEventListener('resize', onResize);

    const previousOverflow = el?.style.overflow ?? '';
    const previousInert = el?.hasAttribute('inert') ?? false;
    const previousAriaHidden = el?.getAttribute('aria-hidden') ?? null;
    if (el) {
      el.style.overflow = 'hidden';
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    }

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', onResize);
      if (el) {
        el.style.overflow = previousOverflow;
        if (!previousInert) el.removeAttribute('inert');
        if (previousAriaHidden === null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', previousAriaHidden);
      }
    };
  }, [locked]);

  // Push-back is applied and removed as inline styles only for the duration
  // of a takeover. `[data-app-main]` carries its own `transition-all` for the
  // side-nav offset; an inline `transition` left behind would override it
  // app-wide, so it is cleared once the un-push has finished animating.
  const wasPushedRef = React.useRef(false);
  React.useEffect(() => {
    const el = document.querySelector('[data-app-main]') as HTMLElement | null;
    if (!el) return;
    if (!pushed && !wasPushedRef.current) return;

    el.style.transition = reducedMotion
      ? `opacity ${PUSH_TRANSITION_MS}ms ease-out`
      : `transform ${PUSH_TRANSITION_MS}ms ease-out, opacity ${PUSH_TRANSITION_MS}ms ease-out`;
    el.style.transformOrigin = 'center center';
    el.style.transform = pushed && !reducedMotion ? 'scale(0.98)' : '';
    el.style.opacity = pushed ? '0.55' : '';
    wasPushedRef.current = pushed;
    if (pushed) return;

    const timer = setTimeout(() => {
      el.style.transition = '';
      el.style.transformOrigin = '';
    }, PUSH_TRANSITION_MS);
    return () => {
      clearTimeout(timer);
      // The timer above never got to run — clear the two properties here
      // instead, whether this cleanup fired because of an unmount or because
      // `pushed` flipped back to true before the timer elapsed. A fast
      // re-push's own effect run sets both right back afterward, so this is
      // harmless there; without it, a cancelled timer left `transition`/
      // `transformOrigin` inline on `[data-app-main]` forever (the separate
      // unmount backstop below only covers the `pushed === true` case).
      el.style.transition = '';
      el.style.transformOrigin = '';
    };
  }, [pushed, reducedMotion]);

  // Backstop: an unmount mid-takeover must not leave the page pushed back.
  React.useEffect(
    () => () => {
      if (!wasPushedRef.current) return;
      const el = document.querySelector('[data-app-main]') as HTMLElement | null;
      if (!el) return;
      el.style.transition = '';
      el.style.transformOrigin = '';
      el.style.transform = '';
      el.style.opacity = '';
    },
    []
  );

  return state;
}
